import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Download, Loader2 } from "lucide-react";
import { useRequestUserContactExport } from "@/lib/api/exports";
import {
  EXPORT_QUERY_TYPES,
  getQueryTypeConfig,
  getDefaultParameters,
} from "./config/export-query-types";
import { ExportParameterInput } from "./export-parameter-input";

export function UserContactExportDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [queryType, setQueryType] = useState<string>("users-active-months");
  const [parameters, setParameters] = useState<Record<string, any>>({});

  const { mutateAsync: requestExport, isPending } = useRequestUserContactExport();

  // Initialize parameters when query type changes
  useEffect(() => {
    const defaultParams = getDefaultParameters(queryType);
    setParameters(defaultParams);
  }, [queryType]);

  const handleParameterChange = (key: string, value: any) => {
    setParameters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleConfirm = async () => {
    try {
      await requestExport({ queryType, parameters });
      setIsOpen(false);
    } catch (error) {
      console.error("Export request failed:", error);
    }
  };

  const currentConfig = getQueryTypeConfig(queryType);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Download className="h-4 w-4" />
          Export User Contact
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Export User Contact Data</DialogTitle>
          <DialogDescription>
            Select the query type and configure parameters for the export. The process
            may take several minutes to complete. You will be notified when the
            export is ready for download.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="query-type">Query Type</Label>
            <Select value={queryType} onValueChange={setQueryType}>
              <SelectTrigger>
                <SelectValue placeholder="Select query type" />
              </SelectTrigger>
              <SelectContent>
                {EXPORT_QUERY_TYPES.map((config) => (
                  <SelectItem key={config.id} value={config.id}>
                    {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {currentConfig && (
            <div className="space-y-4">
              <div className="rounded-md bg-blue-50 p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">
                  {currentConfig.label}
                </h4>
                <p className="text-sm text-blue-700">
                  {currentConfig.description}
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-medium">Parameters</h4>
                <div className="grid grid-cols-2 gap-4">
                  {currentConfig.parameters.map((paramConfig) => (
                    <div
                      key={paramConfig.key}
                      className={paramConfig.gridCols === 2 ? "col-span-2" : ""}
                    >
                      <ExportParameterInput
                        config={paramConfig}
                        value={parameters[paramConfig.key]}
                        onChange={(value) => handleParameterChange(paramConfig.key, value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
          <Button
            variant="outline"
            onClick={() => setIsOpen(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Requesting Export...
              </>
            ) : (
              "Confirm Export"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
